import React, { useEffect, useState, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Spinner, Alert, Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { obtenerPasajerosPorHash } from '../../api/giras.ts';
import { loginCoordinador, obtenerMisGiras } from '../../api/coordinadores.api.ts';
import { obtenerDocumentosCoordinador, descargarDocumentoCoordinador } from '../../api/documentosCoordinador.api.ts';
import { obtenerContactosEmergenciaPublico } from '../../api/contactoEmergencia.api.ts';
import './ListadoGirasPublicaPage.scss';

/**
 * Página pública para visualizar el listado de giras del año en curso
 * Requiere contraseña antes de acceder
 * @returns {JSX.Element} Página de listado de giras
 */
function ListadoGirasPublicaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Estados principales
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [giras, setGiras] = useState([]);
  const [girasFiltradasActual, setGirasFiltradasActual] = useState([]);

  // Estados para login de coordinador
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Estados para sesión de coordinador
  const [sessionToken, setSessionToken] = useState(null);
  const [sessionExpiracion, setSessionExpiracion] = useState(null);

  // Estados de filtros
  const [filtroPrograma, setFiltroPrograma] = useState('');
  const [filtroEstablecimiento, setFiltroEstablecimiento] = useState('');
  const [programas, setProgramas] = useState([]);
  const [establecimientos, setEstablecimientos] = useState([]);

  // Estados del modal de pasajeros
  const [giraSeleccionada, setGiraSeleccionada] = useState(null);
  const [pasajeros, setPasajeros] = useState([]);
  const [loadingPasajeros, setLoadingPasajeros] = useState(false);
  const [showPasajerosModal, setShowPasajerosModal] = useState(false);

  // Estados de documentos
  const [documentos, setDocumentos] = useState([]);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);

  // Estados del modal de ficha
  const [showFichaModal, setShowFichaModal] = useState(false);
  const [selectedPasajero, setSelectedPasajero] = useState(null);
  const [contactosEmergencia, setContactosEmergencia] = useState([]);
  const [loadingContactos, setLoadingContactos] = useState(false);
  // Estados de filtros de pasajeros
  const [filtroAlimentacion, setFiltroAlimentacion] = useState(null);
  const [filtroCumpleaños, setFiltroCumpleaños] = useState(false);
  const [filtroPadecimiento, setFiltroPadecimiento] = useState(false);
  // Tipos de alimentación
  const TIPOS_ALIMENTACION = {
    1: 'Sin Restricción',
    2: 'Vegetariano',
    3: 'Vegano',
    4: 'Intol. Lactosa',
    5: 'Celíaco'
  };

  /**
   * Extrae programas y establecimientos únicos de las giras
   */
  const extraerFiltros = (girasData) => {
    const programasUnicos = [...new Set(girasData.map(g => g.gir_programa).filter(Boolean))];
    const establecimientosUnicos = [...new Set(girasData.map(g => g.est_nombre).filter(Boolean))];
    
    setProgramas(programasUnicos.sort());
    setEstablecimientos(establecimientosUnicos.sort());
  };

  /**
   * Calcula el resumen de alimentación de los pasajeros
   */
  const obtenerResumenAlimentacion = () => {
    const resumen = {
        1: { nombre: 'Sin Restricción', cantidad: 0, color: '#28a745' },
      2: { nombre: 'Vegetariano', cantidad: 0, color: '#ffc107' },
      3: { nombre: 'Vegano', cantidad: 0, color: '#17a2b8' },
      4: { nombre: 'Intol. Lactosa', cantidad: 0, color: '#fd7e14' },
      5: { nombre: 'Celiaco', cantidad: 0, color: '#dc3545' }
    };

    pasajeros.forEach(pasajero => {
      const tipoAlimentacion = Number(pasajero.tal_id);
      if (resumen.hasOwnProperty(tipoAlimentacion)) {
        resumen[tipoAlimentacion].cantidad++;
      }
    });

    return Object.values(resumen);
  };

  /**
   * Verifica si un pasajero tiene enfermedad o tratamiento registrado
   * Considera como "sin padecimiento" si el valor es: vacío, nulo, 'NO' o 'no'
   */
  const tienePadecimiento = (enfermedad, tratamiento) => {
    const esVacio = (valor) => {
      if (!valor) return true;
      const strValor = String(valor).trim().toUpperCase();
      return strValor === '' || strValor === 'NO' || strValor === 'NULL';
    };
    
    return !esVacio(enfermedad) || !esVacio(tratamiento);
  };

  /**
   * Verifica si una fecha de nacimiento cae en el rango de la gira
   */
  const estaEnRangoCumpleaños = (fechaNacimiento, fechaInicio, fechaFin) => {
    if (!fechaNacimiento) return false;

    const cumple = new Date(fechaNacimiento);
    const mesNacimiento = cumple.getMonth();
    const diaNacimiento = cumple.getDate();

    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);

    const cumplesteAño = new Date(inicio.getFullYear(), mesNacimiento, diaNacimiento);
    const cumplesteAñoProximo = new Date(fin.getFullYear(), mesNacimiento, diaNacimiento);

    return (cumplesteAño >= inicio && cumplesteAño <= fin) || 
           (cumplesteAñoProximo >= inicio && cumplesteAñoProximo <= fin);
  };

  /**
   * Obtiene resumen de cumpleaños
   */
  const obtenerResumenCumpleaños = () => {
    if (!giraSeleccionada) return { nombre: 'Cumpleaños', cantidad: 0, color: '#ff6b6b' };
    
    const cantidad = pasajeros.filter(pasajero => 
      estaEnRangoCumpleaños(
        pasajero.alu_fnacimiento,
        giraSeleccionada.gir_fviaje,
        giraSeleccionada.gir_fterminoviaje
      )
    ).length;

    return { nombre: 'Cumpleaños', cantidad, color: '#ff6b6b' };
  };

  /**
   * Obtiene resumen de pasajeros con enfermedad/tratamiento
   */
  const obtenerResumenPadecimiento = () => {
    const cantidad = pasajeros.filter(pasajero => 
      tienePadecimiento(pasajero.alu_enfermedad, pasajero.alu_tratamiento)
    ).length;

    return { nombre: 'Con Padecimiento', cantidad, color: '#e74c3c' };
  };

  /**
   * Obtiene resumen de pasajeros por género (Alumnos y Adultos)
   * Agrupa:
   * - Alumnos: Alumno(a), Alumno(a) 100% Liberado, Alumno(a) 50% Liberado
   * - Adultos: Adulto, Adulto Liberado 100%, Adulto Liberado 50%
   */
  const obtenerResumenPasajerosPorGenero = () => {
    const resumen = {
      alumnos: { femenino: 0, masculino: 0 },
      adultos: { femenino: 0, masculino: 0 }
    };

    pasajeros.forEach(pasajero => {
      const tipoPasajero = (pasajero.tipo_pasajero || '').toLowerCase().trim();
      const genero = (pasajero.genero || '').toLowerCase().trim();

      // Determinar si es alumno o adulto
      const esAlumno = tipoPasajero.includes('alumno');
      const esAdulto = tipoPasajero.includes('adulto');

      // Contar por género
      if (genero === 'femenino' || genero === 'f') {
        if (esAlumno) resumen.alumnos.femenino++;
        if (esAdulto) resumen.adultos.femenino++;
      } else if (genero === 'masculino' || genero === 'm') {
        if (esAlumno) resumen.alumnos.masculino++;
        if (esAdulto) resumen.adultos.masculino++;
      }
    });

    return resumen;
  };

  /**
   * Obtiene pasajeros filtrados seg\u00fan los filtros aplicados
   */
  const obtenerPasajerosFiltrarados = () => {
    let pasajerosFiltrarados = [...pasajeros];

    if (filtroAlimentacion) {
      pasajerosFiltrarados = pasajerosFiltrarados.filter(
        p => Number(p.tal_id) === filtroAlimentacion
      );
    }

    if (filtroCumpleaños) {
      pasajerosFiltrarados = pasajerosFiltrarados.filter(p =>
        estaEnRangoCumpleaños(
          p.alu_fnacimiento,
          giraSeleccionada.gir_fviaje,
          giraSeleccionada.gir_fterminoviaje
        )
      );
    }

    // Filtro por padecimiento (enfermedad/tratamiento)
    if (filtroPadecimiento) {
      pasajerosFiltrarados = pasajerosFiltrarados.filter(p =>
        tienePadecimiento(p.alu_enfermedad, p.alu_tratamiento)
      );
    }

    return pasajerosFiltrarados;
  };

  /**
   * Carga los pasajeros de una gira mediante su QR hash
   */
  const handleCargarPasajeros = async (gira) => {
    if (!gira.qir_hash) {
      Swal.fire('Error', 'Esta gira no tiene código QR generado', 'warning');
      return;
    }
    console.log(gira);
   
    setLoadingPasajeros(true);
    
    try {
      // Usar la contraseña guardada en memoria (aunque no se muestra, fue validada)
      const datos = JSON.parse(sessionStorage.getItem('girasPublicasPassword') || '{}');
      const passwordGuardado = datos.password || password;

      const data = await obtenerPasajerosPorHash(gira.qir_hash, 'Gira2024*');

      if (data.error || !data.success) {
        Swal.fire('Error', 'No se pudieron cargar los pasajeros', 'error');
      } else {
        console.log(data.pasajeros);
         setGiraSeleccionada(data.gira);
        setPasajeros(data.pasajeros || []);
         setDocumentos(data.gira.documentos);
        setShowPasajerosModal(true);
      }
    } catch (err) {
      console.error('Error:', err);
      Swal.fire('Error', 'Error al conectar con el servidor', 'error');
    } finally {
      setLoadingPasajeros(false);
    }
  };

  /**
   * Aplica los filtros a las giras
   */
  useEffect(() => {
    if (authenticated && giras.length > 0) {
      let girasFiltradasTemp = [...giras];

      if (filtroPrograma) {
        girasFiltradasTemp = girasFiltradasTemp.filter(g => g.gir_programa === filtroPrograma);
      }

      if (filtroEstablecimiento) {
        girasFiltradasTemp = girasFiltradasTemp.filter(g => g.est_nombre === filtroEstablecimiento);
      }

      setGirasFiltradasActual(girasFiltradasTemp);
    }
  }, [filtroPrograma, filtroEstablecimiento, authenticated, giras]);

  /**
   * Carga las giras asignadas al coordinador
   */
  const cargarMisGiras = useCallback(async (token) => {
    setLoading(true);
    try {
      const resultado = await obtenerMisGiras(token);
      
      if (resultado.success && resultado.data) {
        setGiras(resultado.data);
        setGirasFiltradasActual(resultado.data);
        extraerFiltros(resultado.data);
      } else {
        Swal.fire('Error', 'Error al cargar giras', 'error');
        setAuthenticated(false);
      }
    } catch (err) {
      console.error('Error:', err);
      Swal.fire('Error', 'Error al conectar con el servidor', 'error');
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Valida la sesión del coordinador al montar el componente
   */
  useEffect(() => {
    const validarSesion = async () => {
      try {
        const sessionData = sessionStorage.getItem('coordinador_session');
        if (sessionData) {
          const { token, expiracion } = JSON.parse(sessionData);
          const ahora = new Date().getTime();
          
          if (ahora < expiracion) {
            // Sesión aún válida
            setSessionToken(token);
            setSessionExpiracion(expiracion);
            setAuthenticated(true);
            await cargarMisGiras(token);
          } else {
            // Sesión expirada
            sessionStorage.removeItem('coordinador_session');
            setAuthenticated(false);
          }
        }
      } catch (err) {
        console.error('Error validando sesión:', err);
        sessionStorage.removeItem('coordinador_session');
      }
    };
    
    validarSesion();
  }, [cargarMisGiras]);

  /**
   * Maneja el login del coordinador con email y contraseña
   */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(null);

    if (!email.trim()) {
      setLoginError('Ingrese el email');
      return;
    }

    if (!password.trim()) {
      setLoginError('Ingrese la contraseña');
      return;
    }

    setLoading(true);
    try {
      const resultado = await loginCoordinador(email, password);

      if (resultado.success && resultado.data && resultado.data.token) {
        const expiracion = new Date().getTime() + (resultado.data.expiracion ? 
          (new Date(resultado.data.expiracion).getTime() - new Date().getTime()) : 
          3600000); // 1 hora por defecto

        // Guardar sesión en sessionStorage
        sessionStorage.setItem('coordinador_session', JSON.stringify({
          token: resultado.data.token,
          usuario: resultado.data.usuario,
          expiracion
        }));

        setSessionToken(resultado.data.token);
        setSessionExpiracion(expiracion);
        setAuthenticated(true);
        setEmail('');
        setPassword('Gira2024*');
        
        // Cargar las giras del coordinador
        await cargarMisGiras(resultado.data.token);
      } else {
        setLoginError(resultado.msg || 'Email o contraseña incorrectos');
      }
    } catch (err) {
      console.error('Error:', err);
      setLoginError('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Maneja el logout del coordinador
   */
  const handleLogout = () => {
    sessionStorage.removeItem('coordinador_session');
    setAuthenticated(false);
    setSessionToken(null);
    setSessionExpiracion(null);
    setGiras([]);
    setGirasFiltradasActual([]);
    setEmail('');
    setPassword('');
    setLoginError(null);
  };

  /**
   * Carga los documentos de la gira cuando se abre el modal
   */
  useEffect(() => {
    if (showPasajerosModal && giraSeleccionada) {
      const cargarDocs = async () => {
        setLoadingDocumentos(true);
        try {
          const resultado = await obtenerDocumentosCoordinador(giraSeleccionada.gir_id);
          if (resultado.success && resultado.data) {
            setDocumentos(resultado.data);
          } else {
            setDocumentos([]);
          }
        } catch (error) {
          console.error('Error al cargar documentos:', error);
          setDocumentos([]);
        } finally {
          setLoadingDocumentos(false);
        }
      };
      //cargarDocs();
    }
  }, [showPasajerosModal, giraSeleccionada]);

  /**
   * Accede al listado de pasajeros de una gira
   */
  const handleAccederGira = (gira) => {
    handleCargarPasajeros(gira);
  };

  /**
   * Abre el modal con detalles del pasajero y carga sus contactos de emergencia
   */
  const handleVerFicha = async (pasajero) => {
    setSelectedPasajero(pasajero);
    setShowFichaModal(true);
    
    // Cargar contactos de emergencia del pasajero
    setLoadingContactos(true);
    try {
      const resultado = await obtenerContactosEmergenciaPublico(pasajero.alu_id);
     
      if (resultado.data) {
         console.log('Resultado de contactos de emergencia:', resultado);
        setContactosEmergencia(resultado.data);
      } else {
        setContactosEmergencia([]);
      }
    } catch (error) {
      console.error('Error al cargar contactos:', error);
      setContactosEmergencia([]);
    } finally {
      setLoadingContactos(false);
    }
  };

  /**
   * Descarga un documento de coordinador
   */
  const handleDescargarDocumento = async (documento) => {
     try {
            await descargarDocumentoCoordinador(documento.doc_id);
            } catch (error) {
                console.error("Error al descargar:", error);
                Swal.fire("Error", "Error al descargar el documento", "error");
            }
  };

  /**
   * Cierra el modal de ficha
   */
  const handleCerrarFicha = () => {
    setShowFichaModal(false);
    setSelectedPasajero(null);
  };

  /**
   * Pantalla de ingreso de coordinador
   */
  if (!authenticated) {
    return (
      <div className="password-screen">
        <Container fluid className="h-100">
          <Row className="h-100 align-items-center">
            <Col xs={12} sm={10} md={8} lg={6} className="mx-auto">
              <Card className="password-card shadow-lg">
                <Card.Body className="p-4 p-sm-5">
                  <div className="text-center mb-4">
                    <i className="material-icons password-icon">admin_panel_settings</i>
                    <h1 className="mt-3 mb-2">{'Acceso Coordinador'}</h1>
                    <p className="text-muted">{'Ingrese sus credenciales para ver sus giras'}</p>
                  </div>

                  {error && <Alert variant="danger">{error}</Alert>}
                  {loginError && <Alert variant="danger">{loginError}</Alert>}

                  <Form onSubmit={handleLogin}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        {'Email'}
                      </Form.Label>
                      <Form.Control
                        type="email"
                        placeholder={'Ingrese su email'}
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setLoginError(null);
                        }}
                        isInvalid={!!loginError}
                        disabled={loading}
                        autoFocus
                      />
                      {loginError && (
                        <Form.Control.Feedback type="invalid" className="d-block">
                          {loginError}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        {'Contraseña'}
                      </Form.Label>
                      <div className="input-group password-input-group">
                        <Form.Control
                          type={showPassword ? 'text' : 'password'}
                          placeholder={'Ingrese su contraseña'}
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            setLoginError(null);
                          }}
                          isInvalid={!!loginError}
                          className="password-field"
                          disabled={loading}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={loading}
                        >
                          <i className="material-icons">
                            {showPassword ? 'visibility_off' : 'visibility'}
                          </i>
                        </Button>
                      </div>
                    </Form.Group>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-100"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" animation="border" />
                          Ingresando...
                        </>
                      ) : (
                        <>
                          <i className="material-icons me-2">login</i>
                          {'Ingresar'}
                        </>
                      )}
                    </Button>
                  </Form>

                  <hr className="my-4" />

                  <div className="text-center">
                    <small className="text-muted">
                      <i className="material-icons" style={{ fontSize: '0.9rem' }}>
                        info
                      </i>
                      {'Sesión con duración de 1 hora'}
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  /**
   * Pantalla de listado de giras
   */
  return (
    <Container fluid className="giras-publico py-4 py-sm-5">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="header-titulo">
            <h1 className="text-primary mb-1">
              <i className="material-icons">flight_takeoff</i>
              {'Listado de Giras'}
            </h1>
            <p className="text-muted mb-0">{'Año en curso'}</p>
          </div>
        </Col>
        <Col xs="auto">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleLogout}
          >
            <i className="material-icons">exit_to_app</i>
            Salir
          </Button>
        </Col>
      </Row>

      {/* Filtros */}
      <Row className="mb-4">
        <Col xs={12} lg={10}>
          <Card className="filtros-card shadow-sm">
            <Card.Body className="p-3 p-sm-4">
              <Row className="g-3">
                <Col xs={12} sm={6} md={5}>
                  <Form.Group>
                    <Form.Label className="fw-bold small">
                      <i className="material-icons">category</i>
                      {'Programa'}
                    </Form.Label>
                    <Form.Select
                      value={filtroPrograma}
                      onChange={(e) => setFiltroPrograma(e.target.value)}
                      className="form-select-filtro"
                    >
                      <option value="">{'Todos los programas'}</option>
                      {programas.map((prog, idx) => (
                        <option key={idx} value={prog}>{prog}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6} md={5}>
                  <Form.Group>
                    <Form.Label className="fw-bold small">
                      <i className="material-icons">business</i>
                      {'Establecimiento'}
                    </Form.Label>
                    <Form.Select
                      value={filtroEstablecimiento}
                      onChange={(e) => setFiltroEstablecimiento(e.target.value)}
                      className="form-select-filtro"
                    >
                      <option value="">{'Todos los establecimientos'}</option>
                      {establecimientos.map((est, idx) => (
                        <option key={idx} value={est}>{est}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={2} className="d-flex align-items-end">
                  <Button
                    variant="outline-secondary"
                    className="w-100"
                    size="sm"
                    onClick={() => {
                      setFiltroPrograma('');
                      setFiltroEstablecimiento('');
                    }}
                  >
                    <i className="material-icons">refresh</i>
                    Limpiar
                  </Button>
                </Col>
              </Row>
              <div className="mt-3 text-muted small">
                <i className="material-icons" style={{ fontSize: '1rem' }}>info</i>
                {'Mostrando '}<strong>{girasFiltradasActual.length}</strong>{' de '}<strong>{giras.length}</strong>{' giras'}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabla de Giras - Desktop */}
      <Row className="mb-4 d-none d-lg-block">
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-light">
              <Card.Title className="mb-0">
                <i className="material-icons">table_chart</i>
                Giras ({girasFiltradasActual.length})
              </Card.Title>
            </Card.Header>
            <Card.Body className="p-0">
              {girasFiltradasActual && girasFiltradasActual.length > 0 ? (
                <div className="table-responsive">
                  <Table striped hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>#</th>
                        <th>Curso</th>
                        <th>Programa</th>
                        <th>Establecimiento</th>
                        <th>Inicio</th>
                        <th>Término</th>
                        <th className="text-center">Pasajeros</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {girasFiltradasActual.map((gira, index) => (
                        <tr key={gira.gir_id}>
                          <td className="fw-bold">{index + 1}</td>
                          <td>{gira.gir_curso}</td>
                          <td>{gira.gir_programa}</td>
                          <td>{gira.est_nombre}</td>
                          <td>{new Date(gira.gir_fviaje).toLocaleDateString('es-CL')}</td>
                          <td>{new Date(gira.gir_fterminoviaje).toLocaleDateString('es-CL')}</td>
                          <td className="text-center">
                            <span className="badge bg-info">{gira.total_pasajeros || 0}</span>
                          </td>
                          <td className="text-center">
                            {gira.qir_hash ? (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleAccederGira(gira)}
                              >
                                <i className="material-icons">visibility</i>
                                Ver Pasajeros
                              </Button>
                            ) : (
                              <span className="text-muted small">Sin QR</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <Alert variant="info" className="mb-0">
                  No hay giras que coincidan con los filtros seleccionados
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tarjetas de Giras - Mobile/Tablet */}
      <Row className="d-lg-none">
        {girasFiltradasActual && girasFiltradasActual.length > 0 ? (
          girasFiltradasActual.map((gira, index) => (
            <Col xs={12} sm={6} md={4} key={gira.gir_id} className="mb-3">
              <Card className="gira-card h-100 shadow-sm">
                <Card.Body>
                  <div className="gira-number">#{index + 1}</div>
                  <h5 className="card-title mb-2">{gira.gir_nombre}</h5>

                  <div className="gira-info mb-3">
                    <div className="info-row">
                      <small className="text-muted">Programa</small>
                      <p className="mb-0">{gira.gir_programa}</p>
                    </div>
                    <div className="info-row">
                      <small className="text-muted">Establecimiento</small>
                      <p className="mb-0">{gira.est_nombre}</p>
                    </div>
                    <div className="info-row">
                      <small className="text-muted">Inicio</small>
                      <p className="mb-0">{new Date(gira.gir_fviaje).toLocaleDateString('es-CL')}</p>
                    </div>
                    <div className="info-row">
                      <small className="text-muted">Pasajeros</small>
                      <p className="mb-0">
                        <span className="badge bg-info">{gira.total_pasajeros || 0}</span>
                      </p>
                    </div>
                  </div>

                  {gira.qir_hash ? (
                    <Button
                      variant="primary"
                      size="sm"
                      className="w-100"
                      onClick={() => handleAccederGira(gira)}
                    >
                      <i className="material-icons">visibility</i>
                      Ver Pasajeros
                    </Button>
                  ) : (
                    <Alert variant="warning" className="mb-0 py-2">
                      <small>Sin código QR generado</small>
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))
        ) : (
          <Col xs={12}>
            <Alert variant="info">
              No hay giras que coincidan con los filtros seleccionados
            </Alert>
          </Col>
        )}
      </Row>

      {/* Modal de Pasajeros */}
      <Modal
        show={showPasajerosModal}
        onHide={() => setShowPasajerosModal(false)}
        fullscreen="lg-down"
        size="xl"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            {giraSeleccionada && (
              <>
                <i className="material-icons me-2">group</i>
                Pasajeros - {giraSeleccionada.est_nombre} {giraSeleccionada.gir_curso}
              </>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          {/* Información de la Gira - SIEMPRE SE MUESTRA */}
          {giraSeleccionada && (
            <Row className="mb-4">
              <Col xs={12}>
                <Card className="gira-info-card shadow-sm border-primary">
                  <Card.Body className="p-3">
                    {/* Información General de la Gira */}
                    <h6 className="fw-bold text-primary mb-3">
                      <i className="material-icons me-2" style={{ fontSize: '1.1rem' }}>info_outline</i>
                      Detalles de la Gira
                    </h6>
                    <Row className="g-2 mb-3">
                      {giraSeleccionada.gir_ano && (
                        <Col xs={6} sm={4} md={3}>
                          <small className="text-muted d-block fw-bold">Año</small>
                          <small>{giraSeleccionada.gir_ano}</small>
                        </Col>
                      )}
                      {giraSeleccionada.gir_programa && (
                        <Col xs={6} sm={4} md={3}>
                          <small className="text-muted d-block fw-bold">Programa</small>
                          <small>{giraSeleccionada.gir_programa}</small>
                        </Col>
                      )}
                      {giraSeleccionada.gir_tramo && (
                        <Col xs={6} sm={4} md={3}>
                          <small className="text-muted d-block fw-bold">Tramo</small>
                          <small>{giraSeleccionada.gir_tramo}</small>
                        </Col>
                      )}
                      {giraSeleccionada.gir_region && (
                        <Col xs={6} sm={4} md={3}>
                          <small className="text-muted d-block fw-bold">Región</small>
                          <small>{giraSeleccionada.gir_region}</small>
                        </Col>
                      )}
                      {giraSeleccionada.gir_comuna && (
                        <Col xs={6} sm={4} md={3}>
                          <small className="text-muted d-block fw-bold">Comuna</small>
                          <small>{giraSeleccionada.gir_comuna}</small>
                        </Col>
                      )}
                      {giraSeleccionada.est_nombre && (
                        <Col xs={6} sm={4} md={3}>
                          <small className="text-muted d-block fw-bold">Establecimiento</small>
                          <small>{giraSeleccionada.est_nombre}</small>
                        </Col>
                      )}
                      {giraSeleccionada.gir_curso && (
                        <Col xs={6} sm={4} md={3}>
                          <small className="text-muted d-block fw-bold">Curso</small>
                          <small>{giraSeleccionada.gir_curso}</small>
                        </Col>
                      )}
                      {giraSeleccionada.gir_liberados && (
                        <Col xs={6} sm={4} md={3}>
                          <small className="text-muted d-block fw-bold">Liberados</small>
                          <small>{giraSeleccionada.gir_liberados}</small>
                        </Col>
                      )}
                      {giraSeleccionada.gir_fviaje && (
                        <Col xs={6} sm={4} md={3}>
                          <small className="text-muted d-block fw-bold">Fecha Viaje</small>
                          <small>{new Date(giraSeleccionada.gir_fviaje).toLocaleDateString('es-CL')}</small>
                        </Col>
                      )}
                      {giraSeleccionada.gir_fterminoviaje && (
                        <Col xs={6} sm={4} md={3}>
                          <small className="text-muted d-block fw-bold">Fecha Término Viaje</small>
                          <small>{new Date(giraSeleccionada.gir_fterminoviaje).toLocaleDateString('es-CL')}</small>
                        </Col>
                      )}
                      {giraSeleccionada.gir_resp_curso_nombre && (
                        <Col xs={6} sm={4} md={3}>
                          <small className="text-muted d-block fw-bold">Responsable Curso</small>
                          <small>{giraSeleccionada.gir_resp_curso_nombre}</small>
                        </Col>
                      )}
                      {giraSeleccionada.gir_resp_curso_celular && (
                        <Col xs={6} sm={4} md={3}>
                          <small className="text-muted d-block fw-bold">Celular Responsable</small>
                          <small>
                            <a href={`tel:${giraSeleccionada.gir_resp_curso_celular}`} className="link-primary">
                              {giraSeleccionada.gir_resp_curso_celular}
                            </a>
                          </small>
                        </Col>
                      )}
                      {giraSeleccionada.gir_resp_curso_email && (
                        <Col xs={6} sm={4} md={3}>
                          <small className="text-muted d-block fw-bold">Email Responsable</small>
                          <small>
                            <a href={`mailto:${giraSeleccionada.gir_resp_curso_email}`} className="link-primary">
                              {giraSeleccionada.gir_resp_curso_email}
                            </a>
                          </small>
                        </Col>
                      )}
                      {giraSeleccionada.gir_voucher_seguro && (
                        <Col xs={6} sm={4} md={3}>
                          <small className="text-muted d-block fw-bold">N° Voucher Seguro Médico</small>
                          <small>{giraSeleccionada.gir_voucher_seguro}</small>
                        </Col>
                      )}
                      
                       <Col xs={12} sm={6} md={2.4}>
                                          <div className="info-item">
                                            <small className="text-muted d-block fw-bold">Total Pasajeros</small>
                                            <p className="mb-0 fs-5">
                                              <span className="badge bg-primary">{giraSeleccionada.total_pasajeros}</span>
                                            </p>
                                          </div>
                                        </Col>
                    </Row>
                    
                    <hr className="my-2" />

                    {/* Información de Transporte */}
                    {(giraSeleccionada.gir_tipo_transporte || giraSeleccionada.gir_aerolinea || giraSeleccionada.gir_num_vuelo) && (
                      <>
                        <h6 className="fw-bold text-primary mb-2">
                          <i className="material-icons me-2" style={{ fontSize: '1.1rem' }}>flight_takeoff</i>
                          Información de Transporte
                        </h6>
                        <Row className="g-2 mb-3">
                          {giraSeleccionada.gir_tipo_transporte && (
                            <Col xs={6} sm={4} md={3}>
                              <small className="text-muted d-block fw-bold">Tipo</small>
                              <small>{giraSeleccionada.gir_tipo_transporte}</small>
                            </Col>
                          )}
                          {giraSeleccionada.gir_aerolinea && (
                            <Col xs={6} sm={4} md={3}>
                              <small className="text-muted d-block fw-bold">Aerolínea</small>
                              <small>{giraSeleccionada.gir_aerolinea}</small>
                            </Col>
                          )}
                          {giraSeleccionada.gir_codigo_reserva_vuelo && (
                            <Col xs={6} sm={4} md={3}>
                              <small className="text-muted d-block fw-bold">Código Reserva Vuelo</small>
                              <small>{giraSeleccionada.gir_codigo_reserva_vuelo}</small>
                            </Col>
                          )}
                          {giraSeleccionada.gir_num_vuelo && (
                            <Col xs={6} sm={4} md={3}>
                              <small className="text-muted d-block fw-bold">Vuelo</small>
                              <small>{giraSeleccionada.gir_num_vuelo}</small>
                            </Col>
                          )}
                          {giraSeleccionada.gir_horario_vuelo_ida && (
                            <Col xs={6} sm={4} md={3}>
                              <small className="text-muted d-block fw-bold">Hora Vuelo (Ida)</small>
                              <small>{giraSeleccionada.gir_horario_vuelo_ida}</small>
                            </Col>
                          )}
                          {giraSeleccionada.gir_horario_vuelo_regreso && (
                            <Col xs={6} sm={4} md={3}>
                              <small className="text-muted d-block fw-bold">Hora Vuelo (Regreso)</small>
                              <small>{giraSeleccionada.gir_horario_vuelo_regreso}</small>
                            </Col>
                          )}
                          {giraSeleccionada.gir_patente_bus && (
                            <Col xs={6} sm={4} md={3}>
                              <small className="text-muted d-block fw-bold">Patente Bus</small>
                              <small>{giraSeleccionada.gir_patente_bus}</small>
                            </Col>
                          )}
                        </Row>
                        <hr className="my-2" />
                      </>
                    )}

                    {/* Información de Choferes */}
                    {(giraSeleccionada.gir_nombre_chofer1 || giraSeleccionada.gir_nombre_chofer2) && (
                      <>
                        <h6 className="fw-bold text-primary mb-2">
                          <i className="material-icons me-2" style={{ fontSize: '1.1rem' }}>person_outline</i>
                          Choferes
                        </h6>
                        <Row className="g-2 mb-3">
                          {giraSeleccionada.gir_nombre_chofer1 && (
                            <Col xs={6} sm={4} md={3}>
                              <small className="text-muted d-block fw-bold">Chofer 1</small>
                              <small>{giraSeleccionada.gir_nombre_chofer1}</small>
                            </Col>
                          )}
                          {giraSeleccionada.gir_nombre_chofer2 && (
                            <Col xs={6} sm={4} md={3}>
                              <small className="text-muted d-block fw-bold">Chofer 2</small>
                              <small>{giraSeleccionada.gir_nombre_chofer2}</small>
                            </Col>
                          )}
                        </Row>
                        <hr className="my-2" />
                      </>
                    )}

                    {/* Información de Horarios */}
                    {(giraSeleccionada.gir_horario_salida || giraSeleccionada.gir_horario_regreso || giraSeleccionada.gir_horario_encuentro) && (
                      <>
                        <h6 className="fw-bold text-primary mb-2">
                          <i className="material-icons me-2" style={{ fontSize: '1.1rem' }}>schedule</i>
                          Horarios
                        </h6>
                        <Row className="g-2 mb-3">
                          {giraSeleccionada.gir_horario_encuentro && (
                            <Col xs={6} sm={4} md={3}>
                              <small className="text-muted d-block fw-bold">Encuentro</small>
                              <small>{giraSeleccionada.gir_horario_encuentro}</small>
                            </Col>
                          )}
                          {giraSeleccionada.gir_horario_salida && (
                            <Col xs={6} sm={4} md={3}>
                              <small className="text-muted d-block fw-bold">Salida</small>
                              <small>{giraSeleccionada.gir_horario_salida}</small>
                            </Col>
                          )}
                          {giraSeleccionada.gir_horario_regreso && (
                            <Col xs={6} sm={4} md={3}>
                              <small className="text-muted d-block fw-bold">Regreso</small>
                              <small>{giraSeleccionada.gir_horario_regreso}</small>
                            </Col>
                          )}
                        </Row>
                        <hr className="my-2" />
                      </>
                    )}

                    {/* Información Adicional */}
                    {(giraSeleccionada.gir_hotel || giraSeleccionada.gir_punto_encuentro) && (
                      <Row className="g-2">
                        {giraSeleccionada.gir_hotel && (
                          <Col xs={6} sm={4} md={3}>
                            <small className="text-muted d-block fw-bold">Hotel</small>
                            <small>{giraSeleccionada.gir_hotel}</small>
                          </Col>
                        )}
                        {giraSeleccionada.gir_punto_encuentro && (
                          <Col xs={6} sm={4} md={3}>
                            <small className="text-muted d-block fw-bold">Punto Encuentro</small>
                            <small>{giraSeleccionada.gir_punto_encuentro}</small>
                          </Col>
                        )}
                      </Row>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Sección de Documentos de Coordinador */}
          {giraSeleccionada && (
            <Row className="mb-4">
              <Col xs={12}>
                <Card className="documentos-coordinador-card shadow-sm">
                  <Card.Header className="bg-gradient">
                    <Card.Title className="mb-0">
                      <i className="material-icons me-2">description</i>
                      Documentos de la Gira
                    </Card.Title>
                  </Card.Header>
                  <Card.Body className="p-3">
                    {loadingDocumentos ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" size="sm" className="mb-2" />
                        <p className="text-muted small">Cargando documentos...</p>
                      </div>
                    ) : documentos && documentos.length > 0 ? (
                      <div className="documentos-grid">
                        <div className="table-responsive">
                          <Table striped hover size="sm" className="mb-0">
                            <thead className="bg-light">
                              <tr>
                                <th>Nombre</th>
                                <th>Tipo</th>
                                
                                <th>Acción</th>
                              </tr>
                            </thead>
                            <tbody>
                              {documentos.map((doc, index) => (
                                <tr key={doc.doc_id || index}>
                                  <td className="fw-bold small">
                                    <a onClick={() => handleDescargarDocumento(doc)}  href='#' role="button">
                                    <i className="material-icons" style={{ fontSize: '1rem', marginRight: '0.5rem', verticalAlign: 'middle' }}>
                                      {doc.doc_tipo === 'PDF' ? 'picture_as_pdf' : 
                                       doc.doc_tipo === 'Excel' ? 'description' : 
                                       'insert_drive_file'}
                                    </i>
                                    {doc.doc_nombre}
                                    </a>
                                  </td>
                                  <td>
                                    <span className="badge bg-info">{doc.doc_tipo || 'Documento'}</span>
                                  </td>
                                  
                                  <td >
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleDescargarDocumento(doc)}
                                      title="Descargar documento"
                                    >
                                      <i className="material-icons" style={{ fontSize: '0.9rem' }}>download</i>
                                      Descargar
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    ) : (
                      <Alert variant="info" className="mb-0">
                        <i className="material-icons" style={{ fontSize: '1rem', marginRight: '0.5rem', verticalAlign: 'middle' }}>info</i>
                        No hay documentos disponibles para esta gira
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {loadingPasajeros ? (
            <div className="text-center py-5">
              <Spinner animation="border" className="mb-3" />
              <p className="text-muted">Cargando pasajeros...</p>
            </div>
          ) : pasajeros && pasajeros.length > 0 ? (
            <>

              {/* Resumen de Pasajeros por Género */}
              <Row className="mb-4">
                <Col xs={12}>
                  <Card className="genero-resumen-card shadow-sm">
                    <Card.Header className="bg-gradient">
                      <Card.Title className="mb-0">
                        <i className="material-icons me-2">wc</i>
                        Resumen de Pasajeros por Género
                      </Card.Title>
                    </Card.Header>
                    <Card.Body className="p-3">
                      {(() => {
                        const resumen = obtenerResumenPasajerosPorGenero();
                        return (
                          <Row className="g-3">
                            {/* Alumnos */}
                            <Col xs={12} sm={6} md={3}>
                              <div className="resumen-section">
                                <h6 className="fw-bold text-primary mb-2">
                                  <i className="material-icons" style={{ fontSize: '1.2rem' }}>school</i>
                                  Alumnos
                                </h6>
                                <div className="gender-item mb-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted">Femenino</span>
                                    <span className="badge bg-danger">{resumen.alumnos.femenino}</span>
                                  </div>
                                </div>
                                <div className="gender-item">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted">Masculino</span>
                                    <span className="badge bg-primary">{resumen.alumnos.masculino}</span>
                                  </div>
                                </div>
                                <hr className="my-2" />
                                <div className="d-flex justify-content-between align-items-center fw-bold">
                                  <span>Total</span>
                                  <span className="badge bg-info">{resumen.alumnos.femenino + resumen.alumnos.masculino}</span>
                                </div>
                              </div>
                            </Col>

                            {/* Adultos */}
                            <Col xs={12} sm={6} md={3}>
                              <div className="resumen-section">
                                <h6 className="fw-bold text-primary mb-2">
                                  <i className="material-icons" style={{ fontSize: '1.2rem' }}>person</i>
                                  Adultos
                                </h6>
                                <div className="gender-item mb-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted">Femenino</span>
                                    <span className="badge bg-danger">{resumen.adultos.femenino}</span>
                                  </div>
                                </div>
                                <div className="gender-item">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted">Masculino</span>
                                    <span className="badge bg-primary">{resumen.adultos.masculino}</span>
                                  </div>
                                </div>
                                <hr className="my-2" />
                                <div className="d-flex justify-content-between align-items-center fw-bold">
                                  <span>Total</span>
                                  <span className="badge bg-info">{resumen.adultos.femenino + resumen.adultos.masculino}</span>
                                </div>
                              </div>
                            </Col>

                            {/* Totales Generales */}
                            <Col xs={12} sm={12} md={6}>
                              <div className="resumen-section">
                                <h6 className="fw-bold text-primary mb-2">
                                  <i className="material-icons" style={{ fontSize: '1.2rem' }}>summarize</i>
                                  Totales Generales
                                </h6>
                                <Row className="g-2">
                                  <Col xs={6}>
                                    <div className="total-item text-center p-2 bg-light rounded">
                                      <small className="text-muted d-block fw-bold">Total Femenino</small>
                                      <p className="mb-0 fs-5">
                                        <span className="badge bg-danger">{resumen.alumnos.femenino + resumen.adultos.femenino}</span>
                                      </p>
                                    </div>
                                  </Col>
                                  <Col xs={6}>
                                    <div className="total-item text-center p-2 bg-light rounded">
                                      <small className="text-muted d-block fw-bold">Total Masculino</small>
                                      <p className="mb-0 fs-5">
                                        <span className="badge bg-primary">{resumen.alumnos.masculino + resumen.adultos.masculino}</span>
                                      </p>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </Col>
                          </Row>
                        );
                      })()}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Resumen de alimentación */}
              <Row className="mb-4">
                <Col xs={12}>
                  <Card className="alimentacion-resumen-card shadow-sm">
                    <Card.Header className="bg-gradient">
                      <Card.Title className="mb-0">
                        <i className="material-icons me-2">restaurant</i>
                        Resumen de Alimentación, Cumpleaños y Diagnósticos Médicos
                      </Card.Title>
                    </Card.Header>
                    <Card.Body className="p-3">
                      <Row className="g-2">
                        {/* Resumen de Cumpleaños */}
                        <Col xs={12} sm={6} md={4} lg={2.4} key="cumpleaños" className="mb-2">
                          <div 
                            className={`alimentacion-item text-center ${filtroCumpleaños ? 'active-filter' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setFiltroCumpleaños(!filtroCumpleaños);
                              setFiltroAlimentacion(null);
                              setFiltroPadecimiento(false);
                            }}
                          >
                            <div 
                              className="alimentacion-badge mb-2"
                              style={{ backgroundColor: obtenerResumenCumpleaños().color }}
                            >
                              {obtenerResumenCumpleaños().cantidad}
                            </div>
                            <small className="text-muted d-block fw-bold">Cumpleaños</small>
                          </div>
                        </Col>

                        {/* Resumen de Padecimiento */}
                        <Col xs={12} sm={6} md={4} lg={2.4} key="diagnosticos" className="mb-2">
                          <div 
                            className={`alimentacion-item text-center ${filtroPadecimiento ? 'active-filter' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setFiltroPadecimiento(!filtroPadecimiento);
                              setFiltroAlimentacion(null);
                              setFiltroCumpleaños(false);
                            }}
                          >
                            <div 
                              className="alimentacion-badge mb-2"
                              style={{ backgroundColor: obtenerResumenPadecimiento().color }}
                            >
                              {obtenerResumenPadecimiento().cantidad}
                            </div>
                            <small className="text-muted d-block fw-bold">Con Diagnósticos Médicos</small>
                          </div>
                        </Col>

                        {/* Resumen de Alimentación */}
                        {obtenerResumenAlimentacion().map((item) => {
                          const tipoMap = { 'Sin Restricción': 1, 'Vegetariano': 2, 'Vegano': 3, 'Intol. Lactosa': 4, 'Celiaco': 5 };
                          const tipoId = tipoMap[item.nombre];
                          return (
                            <Col xs={12} sm={6} md={4} lg={2.4} key={item.nombre} className="mb-2">
                              <div 
                                className={`alimentacion-item text-center ${filtroAlimentacion === tipoId ? 'active-filter' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  setFiltroAlimentacion(filtroAlimentacion === tipoId ? null : tipoId);
                                  setFiltroCumpleaños(false);
                                  setFiltroPadecimiento(false);
                                }}
                              >
                                <div 
                                  className="alimentacion-badge mb-2"
                                  style={{ backgroundColor: item.color }}
                                >
                                  {item.cantidad}
                                </div>
                                <small className="text-muted d-block fw-bold">{item.nombre}</small>
                              </div>
                            </Col>
                          );
                        })}
                      </Row>
                      {(filtroAlimentacion || filtroCumpleaños || filtroPadecimiento) && (
                        <div className="mt-3 text-center">
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => {
                              setFiltroAlimentacion(null);
                              setFiltroCumpleaños(false);
                              setFiltroPadecimiento(false);
                            }}
                          >
                            <i className="material-icons" style={{ fontSize: '0.9rem' }}>close</i>
                            Limpiar Filtros
                          </Button>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Tabla de Pasajeros - Desktop */}
              <Row className="mb-4 d-none d-lg-block">
                <Col>
                  <div className="table-responsive">
                    <Table striped hover className="mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th>#</th>
                          <th>Nombre</th>
                          <th>Apellido</th>
                          <th>RUT</th>
                          <th>Tipo Pasajero</th>
                           <th>Género</th>
                          <th className="text-center">Alimentación</th>
                          <th className="text-center">Diagnósticos Médicos</th>
                          <th className="text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {obtenerPasajerosFiltrarados().map((pasajero, index) => {
                          const tienePadc = tienePadecimiento(pasajero.alu_enfermedad, pasajero.alu_tratamiento);

                          return (
                            <tr key={pasajero.alu_id} className={tienePadc ? 'table-info' : ''}>
                              <td className="fw-bold">{index + 1}</td>
                              <td>{pasajero.alu_nombres || '-'} {pasajero.alu_nombre_social && (<span className="badge bg-danger">Nombre Social: {pasajero.alu_nombre_social}</span>)}</td>
                              <td>{pasajero.alu_apellidos || '-'}</td>
                              <td>{pasajero.alu_rut || '-'}</td>
                              <td>{pasajero.tipo_pasajero || '-'}</td>
                              <td>{pasajero.genero || '-'}</td>
                              <td className="text-center">
                                <span className="badge bg-info">{pasajero.tipo_alimentacion}</span>
                              </td>
                              <td className="text-center">
                                {tienePadc && (
                                  <span className="badge bg-warning text-dark">
                                    <i className="material-icons" style={{ fontSize: '0.85rem' }}>health_and_safety</i>
                                    {' '}Diagnósticos Médicos
                                  </span>
                                )}
                              </td>
                              <td className="text-center">
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleVerFicha(pasajero)}
                                >
                                  <i className="material-icons">visibility</i>
                                  Ver
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </Col>
              </Row>

              {/* Tarjetas de Pasajeros - Mobile */}
              <Row className="d-lg-none">
                {obtenerPasajerosFiltrarados().map((pasajero, index) => {
                  const tipoAlimentacion = Number(pasajero.tipo_alimentacion);
                  const nombreAlimentacion = TIPOS_ALIMENTACION[tipoAlimentacion] || 'Desconocido';
                  const tienePadc = tienePadecimiento(pasajero.alu_enfermedad, pasajero.alu_tratamiento);

                  return (
                    <Col xs={12} key={pasajero.alu_id} className="mb-3">
                      <Card className={`shadow-sm ${tienePadc ? 'border-info border-2' : ''}`}>
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                            <div className="flex-grow-1">
                              <h6 className="mb-1">
                                <strong>#{index + 1}</strong> {pasajero.alu_nombres} {pasajero.alu_apellidos}
                              </h6>
                               {pasajero.alu_nombre_social && (
                                <p className="mb-2">
                                  <span className="badge bg-danger">Nombre Social: {pasajero.alu_nombre_social}</span>
                                </p>
                              )}

                              <small className="text-muted d-block">RUT: {pasajero.alu_rut || '-'}</small>
                            </div>
                            <></>
                            <div className="d-flex flex-column gap-2 align-items-end">
                             
                            </div>
                            <div className="d-flex flex-column gap-2 align-items-end">
                             
                              <span className="badge bg-info">{pasajero.tipo_pasajero}</span>
                              <span className="badge bg-info">{pasajero.genero}</span>
                               <span className="badge bg-info">{pasajero.tipo_alimentacion}</span>
                              {tienePadc && (
                                <span className="badge bg-warning text-dark">
                                  <i className="material-icons" style={{ fontSize: '0.85rem' }}>health_and_safety</i>
                                  {' '}Diagnóstico Médico
                                </span>
                              )}
                            </div>
                          </div>
                          <Button
                                                variant="primary"
                                                size="sm"
                                                className="w-100"
                                              onClick={() => handleVerFicha(pasajero)}
                                            >
                                              <i className="material-icons">visibility</i>
                                              Ver Ficha
                                            </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>

              <div className="mt-4 text-muted text-center">
                <small>
                  <i className="material-icons" style={{ fontSize: '1rem' }}>info</i>
                  {' Total: '}<strong>{pasajeros.length}</strong>{' pasajeros'}
                </small>
              </div>
            </>
          ) : (
            <Alert variant="info">No hay pasajeros registrados para esta gira</Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPasajerosModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Ficha del Pasajero */}
      <Modal show={showFichaModal} onHide={handleCerrarFicha} centered className="ficha-modal" size="lg">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="material-icons">person</i>
            Ficha del Pasajero
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {selectedPasajero && (
            <div className="ficha-contenido">
              {/* Header con nombre */}
              <div className="ficha-header text-center bg-light p-4 border-bottom">
                <div className="avatar-ficha mb-3">
                  <i className="material-icons">person_outline</i>
                </div>
                <h4 className="mb-1 fw-bold">
                  {selectedPasajero.alu_nombres} {selectedPasajero.alu_apellidos}
                </h4>
               {selectedPasajero.alu_nombre_social && (
                  <p className="mb-2">
                    <span className="badge bg-danger">Nombre Social: {selectedPasajero.alu_nombre_social}</span>
                  </p>
                )}
                <small className="text-muted">RUT: {selectedPasajero.alu_rut}-{selectedPasajero.alu_dv}</small>
              </div>

              {/* Contenido con tabs/secciones */}
              <div className="ficha-body p-4">
                {/* Sección: Datos del Pasajero */}
                <div className="ficha-section mb-4">
                  <h6 className="fw-bold text-primary mb-3">
                    <i className="material-icons me-2" style={{ fontSize: '1.2rem' }}>person</i>
                    Datos del Pasajero
                  </h6>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">RUT</small>
                        <p className="mb-0">{selectedPasajero.alu_rut}-{selectedPasajero.alu_dv}</p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Tipo de Pasajero</small>
                        <p className="mb-0">{selectedPasajero.tipo_pasajero || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Nacionalidad</small>
                        <p className="mb-0">{selectedPasajero.alu_nacionalidad || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Género</small>
                        <p className="mb-0">{selectedPasajero.genero || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Fecha de Nacimiento</small>
                        <p className="mb-0">
                          {selectedPasajero.alu_fnacimiento 
                            ? new Date(selectedPasajero.alu_fnacimiento).toLocaleDateString('es-CL')
                            : 'N/A'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Email</small>
                        <p className="mb-0">
                          {selectedPasajero.alu_correo ? (
                            <a href={`mailto:${selectedPasajero.alu_correo}`} className="link-primary">
                              {selectedPasajero.alu_correo}
                            </a>
                          ) : (
                            'N/A'
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <hr />

                {/* Sección: Ficha Médica */}
                <div className="ficha-section mb-4">
                  <h6 className="fw-bold text-primary mb-3">
                    <i className="material-icons me-2" style={{ fontSize: '1.2rem' }}>medical_services</i>
                    Ficha Médica
                  </h6>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Tipo Alimentación</small>
                        <p className="mb-0">{selectedPasajero.tipo_alimentacion || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Grupo Sanguíneo</small>
                        <p className="mb-0">{selectedPasajero.grupo_sanguineo || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Talla Polera</small>
                        <p className="mb-0">{selectedPasajero.talla_polera || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">N° Voucher Seguro Médico</small>
                        <p className="mb-0">{giraSeleccionada.gir_voucher_seguro || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="col-12 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Alergias</small>
                        <p className="mb-0">{selectedPasajero.alu_alergia || 'No registra'}</p>
                      </div>
                    </div>
                    <div className="col-12 mb-3">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Enfermedad</small>
                        <p className="mb-0">{selectedPasajero.alu_enfermedad || 'No registra'}</p>
                      </div>
                    </div>
                    <div className="col-12 mb-0">
                      <div className="detail-item">
                        <small className="text-muted d-block fw-bold">Tratamiento</small>
                        <p className="mb-0">{selectedPasajero.alu_tratamiento || 'No registra'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <hr />

                {/* Sección: Contacto Adulto Responsable */}
                {selectedPasajero.apo_nombres && (
                  <div className="ficha-section mb-4">
                    <h6 className="fw-bold text-primary mb-3">
                      <i className="material-icons me-2" style={{ fontSize: '1.2rem' }}>contacts</i>
                      Contacto Adulto Responsable
                    </h6>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <div className="detail-item">
                          <small className="text-muted d-block fw-bold">RUT</small>
                          <p className="mb-0">{selectedPasajero.apo_rut}-{selectedPasajero.apo_dv}</p>
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <div className="detail-item">
                          <small className="text-muted d-block fw-bold">Parentesco</small>
                          <p className="mb-0">{selectedPasajero.parentesco || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="col-12 mb-3">
                        <div className="detail-item">
                          <small className="text-muted d-block fw-bold">Nombres y Apellidos</small>
                          <p className="mb-0">{selectedPasajero.apo_nombres} {selectedPasajero.apo_apellidos}</p>
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <div className="detail-item">
                          <small className="text-muted d-block fw-bold">Email</small>
                          <p className="mb-0">
                            {selectedPasajero.apo_email ? (
                              <a href={`mailto:${selectedPasajero.apo_email}`} className="link-primary">
                                {selectedPasajero.apo_email}
                              </a>
                            ) : (
                              'N/A'
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="col-md-6 mb-0">
                        <div className="detail-item">
                          <small className="text-muted d-block fw-bold">Teléfono Celular</small>
                          <p className="mb-0">{selectedPasajero.apo_celular || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <hr />

                {/* Sección: Contacto de Emergencia */}
                <div className="ficha-section">
                  <h6 className="fw-bold text-primary mb-3">
                    <i className="material-icons me-2" style={{ fontSize: '1.2rem' }}>phone_in_talk</i>
                    Contacto de Emergencia
                  </h6>
                  {loadingContactos ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" className="mb-2" />
                      <p className="text-muted small">Cargando contactos...</p>
                    </div>
                  ) : contactosEmergencia && contactosEmergencia.length > 0 ? (
                    <div className="contactos-list">
                      {contactosEmergencia.map((contacto, idx) => (
                        <div key={contacto.cem_id || idx} className="contacto-item mb-4 pb-3 border-bottom">
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <div className="detail-item">
                                <small className="text-muted d-block fw-bold">RUT</small>
                                <p className="mb-0">{contacto.cem_rut}-{contacto.cem_dv}</p>
                              </div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <div className="detail-item">
                                <small className="text-muted d-block fw-bold">Parentesco</small>
                                <p className="mb-0">{contacto.cem_parentesco || 'N/A'}</p>
                              </div>
                            </div>
                            <div className="col-12 mb-3">
                              <div className="detail-item">
                                <small className="text-muted d-block fw-bold">Nombres y Apellidos</small>
                                <p className="mb-0">{contacto.cem_nombres} {contacto.cem_apellidos}</p>
                              </div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <div className="detail-item">
                                <small className="text-muted d-block fw-bold">Email</small>
                                <p className="mb-0">
                                  {contacto.cem_email ? (
                                    <a href={`mailto:${contacto.cem_email}`} className="link-primary">
                                      {contacto.cem_email}
                                    </a>
                                  ) : (
                                    'N/A'
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="col-md-6 mb-0">
                              <div className="detail-item">
                                <small className="text-muted d-block fw-bold">Teléfono Celular</small>
                                <p className="mb-0">{contacto.cem_celular || 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Alert variant="info" className="mb-0">
                      <i className="material-icons" style={{ fontSize: '1rem', marginRight: '0.5rem', verticalAlign: 'middle' }}>info</i>
                      No hay contactos de emergencia registrados
                    </Alert>
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCerrarFicha}>
            Cerrar
          </Button>
          {selectedPasajero?.alu_correo && (
            <Button
              variant="primary"
              href={`mailto:${selectedPasajero.alu_correo}`}
            >
              <i className="material-icons">mail</i>
              Enviar Email al Alumno
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ListadoGirasPublicaPage;
